import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import { BugReport, Feedback as FeedbackIcon } from '@mui/icons-material'
import { useAdminListFeedbackQuery, useAdminUpdateFeedbackMutation, FeedbackTicket } from '@store/api/feedbackApi'
import AdminPageGate from '@components/auth/AdminPageGate'

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  open: 'primary',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'default',
}

const AdminFeedbackDashboard: React.FC = () => {
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [selectedTicket, setSelectedTicket] = useState<FeedbackTicket | null>(null)
  const [newStatus, setNewStatus] = useState('')
  const [resolution, setResolution] = useState('')
  const [updateError, setUpdateError] = useState('')

  const { data, isLoading, error } = useAdminListFeedbackQuery({
    type: typeFilter || undefined,
    status: statusFilter || undefined,
  })
  const [updateTicket, { isLoading: updating }] = useAdminUpdateFeedbackMutation()

  const tickets = data?.data || []

  const handleOpenDialog = (ticket: FeedbackTicket) => {
    setSelectedTicket(ticket)
    setNewStatus(ticket.status)
    setResolution(ticket.resolution || '')
    setUpdateError('')
  }

  const handleUpdate = async () => {
    if (!selectedTicket) return
    try {
      await updateTicket({
        id: selectedTicket.id,
        status: newStatus,
        resolution: resolution || undefined,
      }).unwrap()
      setSelectedTicket(null)
    } catch (err: any) {
      setUpdateError(err?.data?.message || 'Failed to update ticket')
    }
  }

  return (
    <AdminPageGate permission="support:read" title="User Feedback">
    <Box p={3}>
      <Typography variant="h4" component="h1" gutterBottom>
        Feedback &amp; Bug Reports
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} label="Type">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="bug">Bug Reports</MenuItem>
                <MenuItem value="feedback">Feedback</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={() => { setTypeFilter(''); setStatusFilter('') }}
              sx={{ alignSelf: 'center' }}
            >
              Clear Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && <LinearProgress />}
      {error && <Alert severity="error">Failed to load tickets</Alert>}

      {!isLoading && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No tickets found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {tickets.map((ticket) => (
                <TableRow key={ticket.id}>
                  <TableCell>
                    <Typography variant="caption" fontWeight="bold">{ticket.referenceNumber}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {ticket.type === 'bug'
                        ? <BugReport fontSize="small" color="error" />
                        : <FeedbackIcon fontSize="small" color="primary" />}
                      <Typography variant="caption">{ticket.type}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{ticket.subject}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {ticket.user?.email || ticket.user?.username || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {ticket.severity && <Chip label={ticket.severity} size="small" />}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.status.replace('_', ' ')}
                      size="small"
                      color={STATUS_COLORS[ticket.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleOpenDialog(ticket)}>
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Manage Dialog */}
      <Dialog open={Boolean(selectedTicket)} onClose={() => setSelectedTicket(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Manage Ticket: {selectedTicket?.referenceNumber}
        </DialogTitle>
        <DialogContent>
          {updateError && <Alert severity="error" sx={{ mb: 2 }}>{updateError}</Alert>}
          <Typography variant="subtitle2" gutterBottom>{selectedTicket?.subject}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{selectedTicket?.description}</Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} label="Status">
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Resolution / Notes"
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            multiline
            rows={3}
            placeholder="Add resolution notes or internal comments"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTicket(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={updating}>
            {updating ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AdminPageGate>
  )
}

export default AdminFeedbackDashboard
